import { SenderType_Enum } from '@weave/schema-gen-ts/dist/schemas/messaging/shared/v1/enums.pb';
import { RelatedType } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v1/enums.pb';
import { GroupParticipant, RelatedID } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v1/models.pb';
import { SMSDataV3 } from '@frontend/api-sms-data';
import { SMSSendV3 } from '@frontend/api-sms-send';
import { HttpError } from '@frontend/fetch';
import { formatPhoneNumberE164 } from '@frontend/phone-numbers';
import { getMessageTypeFromRelatedIds } from './get-message-type-from-related-ids';
import { getRelatedIdsThatMeetRequirements, RelatedIDWithRequirement } from './get-related-ids-that-meet-requirements';

type SendRequest = SMSSendV3.Types.SendIO['input'] & {
  _otherOptions?: {
    threadId: string;
  };
};
type GroupSendRequest = Omit<SMSSendV3.Types.GroupSendIO['input'], 'recipients'> & {
  participants: GroupParticipant[];
  _otherOptions?: {
    threadId: string;
  };
};

type SendMessageCallbacks = {
  onSuccess?: () => void;
  onError?: (error: HttpError) => void;
};

type SendMutationLike = {
  mutateAsync: (request: SendRequest, options?: SendMessageCallbacks) => Promise<SMSSendV3.Types.SendIO['output']>;
};

type GroupSendMutationLike = {
  mutateAsync: (
    request: GroupSendRequest,
    options?: SendMessageCallbacks
  ) => Promise<SMSSendV3.Types.GroupSendIO['output']>;
};

export type BuildSendRequestArgs = Pick<SendRequest, 'body' | 'media' | 'relatedIds'> & {
  groupId: string;
  locationPhone: string;
  personPhone: string;
  contextPersonId?: string;
  primaryContactId?: string;
  userId?: string;
  threadId: string;
};

export const getLabels = (ids: RelatedID[]): SendRequest['labels'] => {
  return ids.reduce<SendRequest['labels']>((acc, curr) => {
    if (curr.type === RelatedType.RELATED_TYPE_FORM) {
      if (acc) {
        acc[curr.type] = curr.id;
      } else {
        acc = { [curr.type]: curr.id };
      }
    }

    return acc;
  }, undefined);
};

export const buildSendRequest = ({
  groupId,
  locationPhone,
  personPhone,
  contextPersonId,
  primaryContactId,
  userId,
  threadId,
  body,
  media,
  relatedIds,
}: BuildSendRequestArgs): SendRequest => {
  return {
    locationId: groupId,
    locationPhone,
    personPhone: formatPhoneNumberE164(personPhone),
    programSlugId: SMSDataV3.Types.KnownProgramSlugIds.MANUAL_MESSAGES,
    personId: contextPersonId || primaryContactId,
    createdBy: userId,
    shortenUrls: true,
    messageType: getMessageTypeFromRelatedIds(relatedIds ?? []),
    labels: getLabels(relatedIds ?? []),
    senderType: SenderType_Enum.HUMAN,
    body,
    media,
    relatedIds,
    _otherOptions: {
      threadId,
    },
  };
};

export type SendMessageContext = {
  isGroupThread: boolean;
  groupParticipants?: GroupParticipant[];
  groupSendMutation: GroupSendMutationLike;
  sendMutation: SendMutationLike;
  groupId: string;
  locationPhone: string;
  personPhone: string;
  contextPersonId?: string;
  primaryContactId?: string;
  userId?: string;
  threadId: string;
};

export type SendMessagePayload = {
  body: string;
  media: Array<{ mediaId: string }>;
  relatedIds: RelatedIDWithRequirement[];
} & SendMessageCallbacks;

export const sendMessage = (context: SendMessageContext, payload: SendMessagePayload) => {
  const { body, media, relatedIds, onSuccess, onError } = payload;
  const resolvedRelatedIds = getRelatedIdsThatMeetRequirements({
    relatedIds,
    body,
  });

  if (context.isGroupThread && context.groupParticipants) {
    return context.groupSendMutation.mutateAsync(
      {
        locationId: context.groupId,
        locationPhone: context.locationPhone,
        body,
        media,
        relatedIds: resolvedRelatedIds,
        participants: context.groupParticipants,
        messageType: getMessageTypeFromRelatedIds(resolvedRelatedIds),
        senderType: SenderType_Enum.HUMAN,
        createdBy: context.userId,
        _otherOptions: { threadId: context.threadId },
      },
      {
        onSuccess,
        onError,
      }
    );
  }

  return context.sendMutation.mutateAsync(
    buildSendRequest({
      groupId: context.groupId,
      locationPhone: context.locationPhone,
      personPhone: context.personPhone,
      contextPersonId: context.contextPersonId,
      primaryContactId: context.primaryContactId,
      userId: context.userId,
      threadId: context.threadId,
      body,
      media,
      relatedIds: resolvedRelatedIds,
    }),
    {
      onSuccess,
      onError,
    }
  );
};
